/**
 * Operational schemas: capacity, waitlist, notifications, contact requests,
 * revision requests, agent invocations, webhook events.
 *
 * PRD-003 DATA-09, DATA-10, DATA-15, DATA-17, PR-02, PR-05, PR-46, PR-54,
 * TECH-12, TECH-22.
 */

import { z } from 'zod';
import { emailSchema, isoTimestampSchema, uuidSchema } from './common.js';
import {
  notificationTypeSchema,
  revisionStatusSchema,
  tierSchema,
} from './enums.js';

/**
 * Capacity caps. PRD-003 DATA-09 + PR-49.
 */
export const capacityCapSchema = z.object({
  id: uuidSchema,
  tier: tierSchema,
  cap: z.number().int().nonnegative(),
  created_at: isoTimestampSchema,
  updated_at: isoTimestampSchema,
});
export type CapacityCap = z.infer<typeof capacityCapSchema>;

/**
 * Waitlist entry. PRD-003 DATA-10 + PR-05.
 */
export const waitlistEntrySchema = z.object({
  id: uuidSchema,
  email: emailSchema,
  note: z.string().nullable(),
  tier: tierSchema,
  created_at: isoTimestampSchema,
  notified_at: isoTimestampSchema.nullable(),
});
export type WaitlistEntry = z.infer<typeof waitlistEntrySchema>;

export const waitlistInsertSchema = z.object({
  email: emailSchema,
  note: z.string().max(2000).nullable().optional(),
  tier: tierSchema,
});
export type WaitlistInsert = z.infer<typeof waitlistInsertSchema>;

/**
 * Contact request from /not-sure form. PRD-003 PR-02.
 */
export const contactRequestSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(200),
  email: emailSchema,
  company: z.string().max(200).nullable(),
  situation: z.string().min(1).max(4000),
  created_at: isoTimestampSchema,
  replied_at: isoTimestampSchema.nullable(),
});
export type ContactRequest = z.infer<typeof contactRequestSchema>;

export const contactRequestInsertSchema = z.object({
  name: z.string().min(1).max(200),
  email: emailSchema,
  company: z.string().max(200).nullable().optional(),
  situation: z.string().min(1).max(4000),
});
export type ContactRequestInsert = z.infer<typeof contactRequestInsertSchema>;

/**
 * Notification. PRD-003 DATA-15 + PR-46.
 *
 * `payload` is intentionally loose JSON; the dispatcher fills it per
 * notification type. Type-specific payload schemas can land later under
 * notification-payloads.ts when the templates harden.
 */
export const notificationSchema = z.object({
  id: uuidSchema,
  recipient_user_id: uuidSchema, // auth.users.id; allows operator notifications too
  subscriber_id: uuidSchema.nullable(),
  type: notificationTypeSchema,
  payload: z.record(z.string(), z.unknown()).default({}),
  read_at: isoTimestampSchema.nullable(),
  created_at: isoTimestampSchema,
});
export type Notification = z.infer<typeof notificationSchema>;

/**
 * Revision request. PRD-003 DATA-08 (extension) + PR-54.
 *
 * `sections` is a string[] of section identifiers pulled from the
 * deliverable structure (e.g., `['competitors[2]', 'positioning_grid']`).
 */
export const revisionRequestSchema = z.object({
  id: uuidSchema,
  task_id: uuidSchema,
  subscriber_id: uuidSchema,
  sections: z.array(z.string()).default([]),
  what_to_change: z.string().min(1),
  why: z.string().nullable(),
  status: revisionStatusSchema.default('pending'),
  created_at: isoTimestampSchema,
  resolved_at: isoTimestampSchema.nullable(),
});
export type RevisionRequest = z.infer<typeof revisionRequestSchema>;

export const revisionRequestInsertSchema = z.object({
  task_id: uuidSchema,
  sections: z.array(z.string()).default([]),
  what_to_change: z.string().min(1),
  why: z.string().nullable().optional(),
});
export type RevisionRequestInsert = z.infer<typeof revisionRequestInsertSchema>;

/**
 * Agent invocation log. PRD-003 TECH-22.
 *
 * Every Studio MCP write emits a row. Surfaces in the operator dashboard
 * "Agent telemetry" panel (AI-14) for the 6-month corpus.
 */
export const agentInvocationSchema = z.object({
  id: uuidSchema,
  task_id: uuidSchema.nullable(),
  subscriber_id: uuidSchema.nullable(),
  agent_type: z.string().min(1), // e.g., 'competitive_landscape_drafter'
  mcp_tool: z.string().min(1), // e.g., 'write_deliverable'
  frameworks_consulted: z.array(z.string()).default([]), // vault paths
  sources_cited: z.array(z.string()).default([]), // labels/refs
  status: z.enum(['success', 'error']),
  latency_ms: z.number().int().nonnegative().nullable(),
  tokens_in: z.number().int().nonnegative().nullable(),
  tokens_out: z.number().int().nonnegative().nullable(),
  error_message: z.string().nullable(),
  created_at: isoTimestampSchema,
});
export type AgentInvocation = z.infer<typeof agentInvocationSchema>;

/**
 * Webhook event idempotency record. PRD-003 TECH-12.
 *
 * Stripe's `event.id` is the primary key. INSERT ... ON CONFLICT DO NOTHING
 * makes the handler idempotent across replays.
 */
export const webhookEventSchema = z.object({
  event_id: z.string().min(1), // PK; Stripe event.id
  type: z.string().min(1),
  processed_at: isoTimestampSchema,
  payload: z.record(z.string(), z.unknown()),
});
export type WebhookEvent = z.infer<typeof webhookEventSchema>;
